import { AppBar, Toolbar, Grid, Typography, Box, IconButton, useMediaQuery, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HistoryIcon from '@mui/icons-material/History';
import { RootState } from '../../../redux/store';
import TextLogo from '../../../logo/textLogo.png';
import Logo from '../../../logo/logo.png';
import FormadaLogo from '../../../logo/logo-formada.png';
import LinosArteleLogo from '../../../logo/logo-linos-artele.png';
import { HomeItemsContainer, LogoImg, TextLogoImg } from '../appBarStyles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { HomeBarItem } from './homeBarItem';
import InfoIcon from '../../../logo/info.png';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { appStyleMode, CompanyTitle } from '../../../helpers/clientConfigs';
import { AppStyle } from '../../../helpers/appStyle';
import MenuIcon from '@mui/icons-material/Menu';
import { LanguageMenu } from './languageMenu';
import { CustomerInfo } from 'orderme-api-integration-client';
import { roundDoubleNumbers } from '../../../helpers/currency';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { CartBarBadgeButton } from '../../shoppingCart/cartBarBadgeButton';
import { closeMenu, openMenu } from '../../../redux/reducers/sideBarReducer';
import { useTranslation } from 'react-i18next';
import { HomeBarLink } from './homeBarLink';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';
import { AboutUsRoute, LoginRoute, NewsRoute, SalesPointsRoute } from '../../../routes/allRoutes';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.appBar.top.background,
  color: theme.palette.appBar.top.color,
  width: 'auto',
  minWidth: '300px',
  paddingRight: '48px',
  '@media (max-width: 600px)': {
    paddingRight: '24px',
  },
}));

export function HomeBar() {
  const routeNavigator = useRouteNavigator();
  const dispatch = useAppDispatch();
  const isUserLoggedIn: boolean = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const anonymousLogin = useAppSelector((state: RootState) => state.authState.anonymousLoginInfo);
  const isPasswordRestored: boolean = useAppSelector((state: RootState) => state.authState.isPasswordRestored);
  const isMenuOpened: boolean = useAppSelector((state: RootState) => state.sideBarState.isMenuOpened);
  const customerInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const isSmallSize = useMediaQuery('(max-width:700px)');
  const isLargeSize = useMediaQuery('(min-width:1400px)');
  const { t } = useTranslation();

  const getLogoImagePath = (): string => {
    switch (appStyleMode) {
      case AppStyle.Formada:
        return FormadaLogo;
      case AppStyle.LinosArtele:
        return LinosArteleLogo;
      default:
        return Logo;
    }
  };

  const handleMenuClick = () => {
    if (isMenuOpened) {
      dispatch(closeMenu());
    } else {
      dispatch(openMenu());
    }
  };

  const mobileToolbar = () => {
    return (
      <AppBar
        sx={{ backgroundColor: 'appBar.top.background', color: 'appBar.top.color', minHeight: '60px' }}
        position="static"
      >
        <Toolbar sx={{ px: '10px' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: '4px', width: '100%' }}>
            <TextLogoImg width={100} src={TextLogo} alt={CompanyTitle} />
            <HomeItemsContainer>
              <HomeBarItem icon={<LocationOnIcon />} open={() => routeNavigator.goToSalesPoints()} />
              <HomeBarItem
                icon={<img height={25} width={25} src={InfoIcon} alt="Info" />}
                open={() => routeNavigator.goToInfo()}
              />
              <HomeBarItem icon={<SearchIcon />} open={() => routeNavigator.goToSearch()} />
              <HomeBarItem
                icon={<HistoryIcon />}
                open={() => routeNavigator.goToOrderHistory()}
                isVisible={isUserLoggedIn && !isPasswordRestored}
              />
              <HomeBarItem icon={<AccountCircleIcon />} open={() => routeNavigator.goToAccount()} />
            </HomeItemsContainer>
          </Stack>
        </Toolbar>
      </AppBar>
    );
  };

  const websiteToolbar = () => {
    return (
      <Box sx={{ width: '100%', zIndex: 1200, maxWidth: 'xl' }} position="fixed">
        <StyledAppBar
          elevation={0}
          sx={{
            height: isLargeSize ? '140px' : '100px',
            pb: '10px',
            pt: isLargeSize && appStyleMode === AppStyle.LinosArtele ? '0px' : '10px',
          }}
          position="static"
        >
          {isLargeSize && (
            <LogoImg
              width={appStyleMode === AppStyle.Formada ? 250 : 200}
              sx={{ mx: 'auto', mb: appStyleMode !== AppStyle.LinosArtele && '10px' }}
              src={getLogoImagePath()}
              alt={CompanyTitle}
              onClick={() => routeNavigator.goHome()}
            />
          )}
          <Grid container sx={{ mt: appStyleMode === AppStyle.LinosArtele && '-15px' }}>
            <Grid size="grow" container>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={handleMenuClick} sx={{ p: '6px', ml: '-6px', height: 'auto' }}>
                  <MenuIcon
                    sx={{
                      color: 'primary.contrastText',
                      width: '50px',
                      height: '50px',
                      '@media (max-width: 400px)': {
                        width: '40px',
                        height: '40px',
                      },
                    }}
                  />
                </IconButton>
                <LanguageMenu />
              </Box>
              {!isSmallSize &&
                (isUserLoggedIn ? userBarItem() : anonymousLogin.isLoggedIn ? anonymousUserBarItem() : loginBarItem())}
            </Grid>
            {isLargeSize ? (
              <Grid size="auto" sx={{ mr: '50px' }}>
                <Stack sx={{ height: '60px' }} direction="row" alignItems="center">
                  <HomeBarLink link={SalesPointsRoute} text={t('salesPoint:salesPoints')} />
                  <HomeBarLink link={NewsRoute} text={t('news:menuTitle')} />
                  <HomeBarLink link={AboutUsRoute} text={t('info:aboutUs')} />
                </Stack>
              </Grid>
            ) : (
              <Box>
                <LogoImg
                  width={appStyleMode === AppStyle.Formada ? 250 : 200}
                  sx={{ ml: '0px' }}
                  src={getLogoImagePath()}
                  alt={CompanyTitle}
                  onClick={() => routeNavigator.goHome()}
                />
              </Box>
            )}
            <Grid
              size="grow"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '210px', mr: '-8px' }}
            >
              {!isSmallSize && <CartBarBadgeButton />}
              <HomeBarItem icon={<SearchIcon />} open={routeNavigator.goToSearch} />
            </Grid>
          </Grid>
        </StyledAppBar>
        {isSmallSize && userAndCartBadgeBar()}
      </Box>
    );
  };

  const userAndCartBadgeBar = () => {
    return (
      <StyledAppBar elevation={0} sx={{ height: '50px' }} position="static">
        <Grid container>
          {isUserLoggedIn ? userBarItem() : anonymousLogin.isLoggedIn ? anonymousUserBarItem() : loginBarItem()}
          <Grid size="grow" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <CartBarBadgeButton />
          </Grid>
        </Grid>
      </StyledAppBar>
    );
  };

  const userBarItem = () => {
    return (
      <Grid size="grow" container direction="row" alignItems="center" sx={{ pl: '4px', flex: 1 }}>
        <Typography sx={{ mr: '5px' }}>{customerInfo.name}</Typography>
        <Stack direction="row" alignItems="center" sx={{ width: 'auto' }}>
          <Typography>{roundDoubleNumbers(customerInfo.credit)} €</Typography>
          {!isPasswordRestored && (
            <IconButton onClick={() => routeNavigator.goToCredits()} sx={{ p: '5px', color: 'white' }}>
              <AddCircleOutlineIcon sx={{ height: '35px', width: '35px' }} />
            </IconButton>
          )}
        </Stack>
      </Grid>
    );
  };

  const anonymousUserBarItem = () => {
    return (
      <Stack direction="row" alignItems="center" sx={{ pl: '4px', flex: 1 }}>
        <Typography sx={{ mr: '5px' }}>{anonymousLogin.phone}</Typography>
      </Stack>
    );
  };

  const loginBarItem = () => {
    return (
      <Box display="flex" alignItems="center" sx={{ ml: '10px' }}>
        <HomeBarLink link={LoginRoute} text={t('account:login')} disableMargin={true} />
      </Box>
    );
  };

  return <Box>{appStyleMode === AppStyle.Mobile ? mobileToolbar() : websiteToolbar()}</Box>;
}
